/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import React, { ReactNode } from 'react';
import { ViewModel } from '../../../../core/viewmodel/ViewModel';
import { MaterialMenu } from '../../../../models/material/MaterialMenu';
import { CourseTableModel } from '../../../../models/components/CourseTableModel';
import { CourseTableRow } from '../../../../types/course/CourseTableRow';
import { COLUMN_FORMAT } from '../../../../types/course/CourseColumn';

export class CourseTableViewModel<T extends CourseTableRow> extends ViewModel {
	public menu: MaterialMenu = new MaterialMenu();
	private courseTableModel: CourseTableModel<T> = new CourseTableModel<T>();

	public getSelectedRow(): T | undefined {
		return this.courseTableModel.selectedRow;
	}

	public toggleMenu(event: React.MouseEvent<HTMLButtonElement>, row: T): void {
		this.menu.toggleMenu(event);
		this.courseTableModel.setSelectedRow(row);
	}

	public isLastNewlyAddedRow(
		highlightFunction: ((rowsLength: number, index: number) => boolean) | undefined,
		rowLength: number,
		index: number,
	): boolean {
		return highlightFunction !== undefined && highlightFunction(rowLength, index);
	}

	public returnCellValue(value: T[keyof T], columnFormat: COLUMN_FORMAT | undefined): ReactNode {
		if (value) {
			return this.distinguishCellFormats(value, columnFormat);
		}

		return '–';
	}

	private distinguishCellFormats(value: T[keyof T], columnFormat: COLUMN_FORMAT | undefined): ReactNode {
		switch (columnFormat) {
			case COLUMN_FORMAT.CHECKMARK:
				return <img src={`${process.env.PUBLIC_URL}/img/checkmark.svg`} alt='checkmark Icon' />;

			case COLUMN_FORMAT.SHORTEN:
				const stringValue = value as unknown as string;
				const maxColumnContentLength = 30;
				return stringValue.length > maxColumnContentLength
					? stringValue.slice(0, maxColumnContentLength) + '…'
					: stringValue;

			default:
				return value as ReactNode;
		}
	}
}
